// Helpers for the tabbed-CONTAINER behaviour: blocks can be assigned to a tab
// (block.tabRef) and only show while that tab is active. The active tab per
// tabs-block is held in a Map<tabsBlockId, tabId> at the page/canvas level so
// the render loop can filter member blocks. Shared by the builder canvas
// (index.tsx) and the public page (PublicCampaign).

export interface TabRef { tabsId: string; tabId: string; }

const BAR_H = 44; // tab-bar height inside a tabs block (must match TabsBlock)

type AnyBlock = {
  id: string;
  type: string;
  tabRef?: TabRef;
  props?: any;
  desktopLayout?: { x: number; y: number; width: number; height: number };
  mobileLayout?: { x: number; y: number; width: number; height: number };
};

export const firstTabId = (tabsBlock?: AnyBlock): string =>
  (tabsBlock?.props?.tabs?.[0]?.id) || '';

/** The active tab id for a tabs block (falls back to its first tab). */
export const activeTabIdFor = (tabsBlock: AnyBlock, active: Record<string, string>): string =>
  active[tabsBlock.id] || firstTabId(tabsBlock);

/**
 * A block is hidden when it belongs to a tab that isn't the active one. An
 * orphaned ref (owner tabs block deleted) is treated as visible so content is
 * never silently lost.
 */
export function isHiddenByTab(
  block: AnyBlock,
  blocks: AnyBlock[],
  active: Record<string, string>,
): boolean {
  const ref = block.tabRef;
  if (!ref) return false;
  const owner = blocks.find(b => b.id === ref.tabsId && b.type === 'tabs');
  if (!owner) return false;
  return ref.tabId !== activeTabIdFor(owner, active);
}

/** The drop region of a tabs block (the area below its tab bar), in canvas px. */
export function tabsContentRegion(tabsBlock: AnyBlock, device: 'desktop' | 'mobile') {
  const l = device === 'desktop' ? tabsBlock.desktopLayout : tabsBlock.mobileLayout;
  if (!l) return null;
  return { x: l.x, y: l.y + BAR_H, width: l.width, height: Math.max(0, l.height - BAR_H) };
}

/**
 * Find the tabs block whose content region contains a point (a dragged
 * block's centre). Used on drag-end to assign the block to the active tab.
 */
export function tabsBlockAt(
  cx: number,
  cy: number,
  blocks: AnyBlock[],
  device: 'desktop' | 'mobile',
): AnyBlock | null {
  // Iterate in reverse so a tabs block drawn later (on top) wins.
  for (let i = blocks.length - 1; i >= 0; i--) {
    const b = blocks[i];
    if (b.type !== 'tabs') continue;
    const r = tabsContentRegion(b, device);
    if (!r) continue;
    if (cx >= r.x && cx <= r.x + r.width && cy >= r.y && cy <= r.y + r.height) return b;
  }
  return null;
}

/** Label of the tab a block is assigned to (for the canvas badge). '' if none. */
export function tabRefLabel(block: AnyBlock, blocks: AnyBlock[]): string {
  const ref = block.tabRef;
  if (!ref) return '';
  const owner = blocks.find(b => b.id === ref.tabsId && b.type === 'tabs');
  const tab = owner?.props?.tabs?.find((t: any) => t.id === ref.tabId);
  return tab?.label || '';
}
